import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { logger } from '@/utils/logger'

import { ApiTaRe280 } from '~/endpoints/ApiTaRe280'
import { ApiTaRe310 } from '~/endpoints/ApiTaRe310'
import { ApiTaRe320 } from '~/endpoints/ApiTaRe320'
import compareSubjectOrder from '~/utils/subjects'

// State types
interface PlacementTestResult {
  placementTestResults: {
    memNum: string
    title: string
    gradeDataResults: {
      subjectCode: string
      answerId: string
      score: string
      average: string
      deviationValue: string
    }[]
    difficultyResults: {
      subjectCode: string
      resultsByQuestion: {
        questionNo: number
        basicsScoreRate: string
        practicalScoreRate: string
      }[]
    }[]
  }
}
const getEmptyPlacementTestResult = (): PlacementTestResult => ({
  placementTestResults: {
    memNum: '',
    title: '',
    gradeDataResults: [],
    difficultyResults: [],
  },
})

interface AbilityTestReport {
  abilityTestReport: {
    scoreList: {
      subjectCode: string
      score: string
    }[]
    choiceSchoolScoreList: {
      choiceSchoolName: string
      averageList: {
        subjectCode: string
        averageScore: string
      }[]
      judgmentSubjectList: {
        subjectForJudgement: string
        judgment: string
        comment: string
      }[]
      choiceSchoolRemark: string
    }[]
  }
}
const getEmptyAbilityTestReport = (): AbilityTestReport => ({
  abilityTestReport: {
    scoreList: [
      {
        subjectCode: '',
        score: '',
      },
    ],
    choiceSchoolScoreList: [
      {
        choiceSchoolName: '',
        averageList: [
          {
            subjectCode: '',
            averageScore: '',
          },
        ],
        judgmentSubjectList: [
          {
            subjectForJudgement: '',
            judgment: '',
            comment: '',
          },
        ],
        choiceSchoolRemark: '',
      },
    ],
  },
})

interface SchoolDetermineListUrl {
  choiceSchoolDetermineListUrl: string
}
const getEmptySchoolDetermineListUrl = (): SchoolDetermineListUrl => ({
  choiceSchoolDetermineListUrl: '',
})

// State action parameter

interface ActionParamsPlacementTestResult {
  fiscalYear: number
  month: number
  grade: number
  courseId: string
}

interface ActionParamsAbilityTestReport {
  fiscalYear: number
  testMonth: number
  testCountPerMonth?: number
  grade: number
  courseId: string
}

interface ActionParamsSchoolDetermineListUrl {
  fiscalYear: number
  month: number
  times: number
  grade: number
}

const JUDGE_SUBJECTID_LIST = ['S2', 'S3P', 'S3J', 'S4'] as const

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'apis/DiagnoseSchool',
})
export default class DiagnoseSchool extends VuexModule {
  // States
  private placementTestResult_ = getEmptyPlacementTestResult()

  private abilityTestReport_ = getEmptyAbilityTestReport()

  private schoolDetermineListUrl_ = getEmptySchoolDetermineListUrl()

  // Getters
  get placementTestResult(): PlacementTestResult {
    return this.placementTestResult_
  }

  get abilityTestReport(): AbilityTestReport {
    return this.abilityTestReport_
  }

  get choiceSchoolDetermineListUrl(): SchoolDetermineListUrl {
    return this.schoolDetermineListUrl_
  }

  // Mutations
  @Mutation
  updatePlacementTestResult(value: PlacementTestResult) {
    this.placementTestResult_ = value
  }

  @Mutation
  updateAbilityTestReport(value: AbilityTestReport) {
    // 必ず４つの情報（2教科、3教科（国算理）、3教科（国算社）、4教科）が含まれてくるわけではなく
    // 例えば4教科のみの情報が返ってくるケースがある。
    // Viewへは必ず４つの情報として返したいため、ここで不足している情報を差し込んでおく。
    if (value) {
      for (const choiseSchoolScore of value.abilityTestReport.choiceSchoolScoreList) {
        for (let i = 0; i < JUDGE_SUBJECTID_LIST.length; i++) {
          const data = choiseSchoolScore.judgmentSubjectList.find(
            obj => obj.subjectForJudgement === JUDGE_SUBJECTID_LIST[i]
          )
          if (data === undefined) {
            choiseSchoolScore.judgmentSubjectList.splice(i, 0, {
              subjectForJudgement: JUDGE_SUBJECTID_LIST[i],
              judgment: '-',
              comment: '-',
            })
          }
        }
      }
    }

    this.abilityTestReport_ = value
  }

  @Mutation
  updateSchoolDetermineListUrl(value: SchoolDetermineListUrl) {
    this.schoolDetermineListUrl_ = value
  }

  // Actions

  @Action
  clear() {
    this.context.commit('updatePlacementTestResult', getEmptyPlacementTestResult())
    this.context.commit('updateAbilityTestReport', getEmptyAbilityTestReport())
    this.context.commit('updateSchoolDetermineListUrl', getEmptySchoolDetermineListUrl())
  }

  @Action
  // API: ApiTaRe280
  async getPlacementTestResult(param: ActionParamsPlacementTestResult) {
    logger.trace('in: getPlacementTestResult')
    const api = new ApiTaRe280.Api()

    const result = await api.get<PlacementTestResult | null>(
      {
        query: {
          fiscalYear: param.fiscalYear,
          month: param.month,
          grade: param.grade,
          courseId: param.courseId,
        },
      },
      // success
      (data, _) => {
        return {
          // placementTestResults: data.placementTestResults,
          placementTestResults: {
            ...data.placementTestResults,
            // testResultListは表示順でソートできるようにstoreの時点で調整する
            gradeDataResults: (data.placementTestResults.gradeDataResults || [])
              .sort((a, b) => {
                return compareSubjectOrder(a.subjectCode, b.subjectCode)
              })
              .filter(item => {
                // NOTE: dropするものがあればここで実施する
                if (item.subjectCode === 'S3J') {
                  return false
                }
                return true
              }),
            difficultyResults: (data.placementTestResults.difficultyResults || [])
              .sort((a, b) => {
                return compareSubjectOrder(a.subjectCode, b.subjectCode)
              })
              .map(item => {
                return {
                  ...item,
                  resultsByQuestion: (item.resultsByQuestion || []).sort((a, b) => {
                    return a.questionNo - b.questionNo
                  }),
                }
              }),
          },
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updatePlacementTestResult', result)
  }

  @Action
  // API: ApiTaRe310
  async getAbilityTestReport(param: ActionParamsAbilityTestReport) {
    logger.trace('in: getAbilityTestReport')
    const api = new ApiTaRe310.Api()

    // API: // Test
    const result = await api.get<AbilityTestReport | null>(
      {
        query: {
          fiscalYear: param.fiscalYear,
          testMonth: param.testMonth,
          testCountPerMonth: param.testCountPerMonth,
          grade: param.grade,
          courseId: param.courseId,
        },
      },
      // success
      (data, _) => {
        return {
          abilityTestReport: {
            ...data.abilityTestReport,
            scoreList: data.abilityTestReport.scoreList
              .sort((a, b) => {
                return compareSubjectOrder(a.subjectCode, b.subjectCode)
              })
              .filter(score => score.subjectCode !== 'S3J'),
            choiceSchoolScoreList: data.abilityTestReport.choiceSchoolScoreList.map(choiceSchoolScore => {
              return {
                ...choiceSchoolScore,
                averageList: choiceSchoolScore.averageList
                  .sort((a, b) => {
                    return compareSubjectOrder(a.subjectCode, b.subjectCode)
                  })
                  .filter(score => score.subjectCode !== 'S3J'),
                judgmentSubjectList: choiceSchoolScore.judgmentSubjectList.sort((a, b) => {
                  return compareSubjectOrder(a.subjectForJudgement, b.subjectForJudgement)
                }),
              }
            }),
          },
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)

        return null
      }
    )
    if (result === null) {
      return
    }

    this.context.commit('updateAbilityTestReport', result)
  }

  @Action
  // API: ApiTaRe320
  async getSchoolDetermineListUrl(param: ActionParamsSchoolDetermineListUrl) {
    logger.trace('in: getSchoolDetermineListUrl')
    const api = new ApiTaRe320.Api()
    const result = await api.get<SchoolDetermineListUrl | null>(
      {
        query: {
          fiscalYear: param.fiscalYear,
          month: param.month,
          times: param.times,
          grade: param.grade,
        },
      },
      // success
      (data, _) => {
        return {
          choiceSchoolDetermineListUrl: data.choiceSchoolDetermineListUrl,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )

    if (result === null) {
      return
    }
    this.context.commit('updateSchoolDetermineListUrl', result)
  }
}
